import React, { FC, useContext, useState } from 'react';
import { BasketDetailsType } from '../../../models/basket-details-type';
import BasketFeaturesType from '../../../models/basket-features-type';
import { BasketTableLabelsType } from '../../../models/basket-table-labels-type';
import { BasketType } from '../../../models/basket-type';
import { CheckoutBasketTableLabelsType } from '../../../models/checkout-basket-table-labels-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import PlacementEnum from '../../../models/placement-enum';
import { ProductBasketLineItemType } from '../../../models/product-basket-line-item-type';
import { ProductLineItemType } from '../../../models/product-line-item-type';
import ProductTypeEnum from '../../../models/product-type-enum';
import ButtonSecondary from '../../atoms/button-secondary/button-secondary';
import BasketEmpty from '../../molecules/basket-empty/basket-empty';
import BasketTable from '../../molecules/basket-table/basket-table';
import { BasketStateContext } from '../../providers/basket/basket.provider';
import useAddToBasket from '../../providers/basket/hooks/use-add-to-basket';
import useClearBasket from '../../providers/basket/hooks/use-clear-basket';
import useDeleteBasketLineItem from '../../providers/basket/hooks/use-delete-basket-line-item';
import useGetConfirmedBackOrderSkus from '../../providers/basket/hooks/use-get-confirmed-back-order-skus';
import useToggleConfirmedBackOrderSku from '../../providers/basket/hooks/use-toggle-confirmed-back-order-sku';
import useUpdateBasketLineItem from '../../providers/basket/hooks/use-update-basket-line-item';
import useUpdateMultipleLineItems from '../../providers/basket/hooks/use-update-multiple-line-items';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';

interface Props extends BasketDetailsType {
  readonly basket: BasketType;
}

const BasketDetails: FC<Props> = ({
  assemblyProductLabel,
  addProductErrorBody,
  addProductErrorDescription,
  addProductErrorHeading,
  insufficientClearanceStockError,
  awaitingStockLabel,
  backOrderAndSendAsSingleDelivery,
  basket,
  bulkSaveAvailableLabel,
  callOffProductLabel,
  checkLeadTimesLabel,
  clearAllButtonText,
  clearanceProductLabel,
  clearQuoteAccept,
  clearQuoteCancel,
  clearQuoteDescription,
  clearQuoteHeading,
  clearQuoteHelp,
  clearQuoteWarning,
  discountColumnLabel,
  emptyBasketDescription,
  emptyBasketTitle,
  hazardousProductLabel,
  heading,
  leadTimesPopupHeading,
  leadTimesPopupLeadTimeColumnLabel,
  leadTimesPopupQuantityColumnLabel,
  nonCatOrAssemblyProductLabel,
  numberInStockLabel,
  priceColumnLabel,
  productDetailsColumnLabel,
  quantityColumnLabel,
  recommendedAlternativesAvailable,
  recommendedAlternativesPopupAddButtonText,
  recommendedAlternativesPopupHeading,
  recommendedAlternativesPopupReplaceButtonText,
  removeButtonLabel,
  stockColumnLabel,
  subTotalColumnLabel,
  supplierProductsHeading,
  updateAllButtonText,
  updateQuantityLinkLabel,
  transientProductLabel,
  viewOptions,
}) => {
  const clearQuoteLabels = {
    clearQuoteHeading,
    clearQuoteHelp,
    clearQuoteDescription,
    clearQuoteCancel,
    clearQuoteAccept,
  };
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const basketState = useContext(BasketStateContext);
  const addLineItem = useAddToBasket(
    {
      addProductErrorBody,
      addProductErrorDescription,
      addProductErrorHeading,
      insufficientClearanceStockError,
    },
    clearQuoteLabels
  );
  const updateLineItem = useUpdateBasketLineItem(
    clearQuoteLabels,
    addProductErrorHeading,
    addProductErrorBody
  );
  const deleteLineItem = useDeleteBasketLineItem();
  const updateMultipleLineItems = useUpdateMultipleLineItems(clearQuoteLabels);
  const clearBasket = useClearBasket(clearQuoteLabels);
  const confirmedBackOrderSkus = useGetConfirmedBackOrderSkus();
  const toggleConfirmedBackOrderSku = useToggleConfirmedBackOrderSku();

  const basketTableLabels: BasketTableLabelsType = {
    priceColumnLabel,
    stockColumnLabel,
    discountColumnLabel,
    quantityColumnLabel,
    subTotalColumnLabel,
    productDetailsColumnLabel,
    updateQuantityLinkLabel,
    removeButtonLabel,
    callOffProductLabel,
  };

  const checkoutBasketTableLabels: CheckoutBasketTableLabelsType = {
    assemblyProductLabel,
    awaitingStockLabel,
    backOrderAndSendAsSingleDelivery,
    bulkSaveAvailableLabel,
    checkLeadTimesLabel,
    clearanceProductLabel,
    emptyBasketDescription,
    emptyBasketTitle,
    hazardousProductLabel,
    leadTimesPopupHeading,
    leadTimesPopupLeadTimeColumnLabel,
    leadTimesPopupQuantityColumnLabel,
    nonCatOrAssemblyProductLabel,
    numberInStockLabel,
    recommendedAlternativesAvailable,
    recommendedAlternativesPopupAddButtonText,
    recommendedAlternativesPopupHeading,
    recommendedAlternativesPopupReplaceButtonText,
    transientProductLabel,
    viewOptions,
  };

  const basketFeatures: BasketFeaturesType = {
    addButton: false,
    checkoutLayout: false,
    discountColumn: true,
    favouritesLayout: false,
    inStockColumn: true,
    productWarnings: true,
    quantityBreaksSave: true,
    quantityReadonly: false,
    recommendedAlternatives: true,
    removeButton: true,
    replaceButton: false,
    subtotalColumn: true,
    updateQuantityButton: true,
  };

  const [uncommittedQuantities, setUncommittedQuantities] = useState<{
    [key: number]: number;
  }>({});

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const mainBasketItems: ProductBasketLineItemType[] = basket?.lineItems
    ? basket?.lineItems.filter(
        (item: ProductLineItemType) =>
          !item.product.properties.supplierOnboarding
      )
    : [];

  const ownCarriageBasketItems: ProductBasketLineItemType[] = basket?.lineItems
    ? basket?.lineItems.filter(
        (item: ProductLineItemType) =>
          !!item.product.properties.supplierOnboarding
      )
    : [];

  const reversedMainBasketItems = mainBasketItems.reverse();
  const reversedOwnCarriageBasketItems = ownCarriageBasketItems.reverse();

  const onLineItemAdd = async (item: ProductLineItemType, quantity: number) => {
    setIsSaving(true);
    await addLineItem(item.product.variantSku, quantity);
    setIsSaving(false);
  };

  const onLineItemChangeQuantity = async (
    item: ProductBasketLineItemType,
    quantity: number
  ) => {
    setUncommittedQuantities({
      ...uncommittedQuantities,
      [item.orderLineId]: quantity,
    });
  };

  const onCommitAllLineItemQuantities = async () => {
    setIsSaving(true);
    try {
      const updatedBasket = await updateMultipleLineItems(
        Object.keys(uncommittedQuantities).map((orderLineId) => ({
          orderLineId: Number(orderLineId),
          newQuantity: uncommittedQuantities[orderLineId] as number,
        }))
      );

      setUncommittedQuantities({});
      setIsSaving(false);
      pushToDataLayer(GtmEventNameEnum.BasketAction);
      pushToDataLayer(GtmEventNameEnum.ProductUpdate, {
        ecommerce: {
          detail: {
            products: updatedBasket?.lineItems.map((lineItem) => {
              return {
                price: lineItem.buyPriceFormatted,
                productImage: lineItem.product.properties.webImageLarge1425,
                productName: lineItem.product.name,
                productCode: lineItem.product.variantSku,
                masterCode: lineItem.product.sku,
                listPrice: lineItem.listPriceFormatted,
                quantity: lineItem.quantitySatisfied,
              };
            }),
          },
        },
        basketValue: updatedBasket?.formattedTotal,
      });
    } catch {
      setIsSaving(false);
    }
  };

  const onLineItemReplace = async (
    item: ProductLineItemType,
    quantity: number,
    replacesItem: ProductLineItemType
  ) => {
    setIsSaving(true);
    await addLineItem(item.product.variantSku, quantity);
    await deleteLineItem(replacesItem.orderLineId);
    setIsSaving(false);
  };

  const onLineItemUpdate = async (
    item: ProductBasketLineItemType,
    quantity: number
  ) => {
    setIsSaving(true);
    try {
      await updateLineItem(item.orderLineId, quantity);
    } catch {
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
  };

  const onLineItemRemove = async (item: ProductBasketLineItemType) => {
    setIsSaving(true);
    const updatedBasket = await deleteLineItem(item.orderLineId);
    setIsSaving(false);

    pushToDataLayer(GtmEventNameEnum.RemoveFromCart, {
      ecommerce: {
        remove: {
          products: updatedBasket.lineItems.map((lineItem) => {
            return {
              price: lineItem.buyPriceFormatted,
              productImage: lineItem.product.properties.webImageLarge1425,
              productName: lineItem.product.name,
              productCode: lineItem.product.variantSku,
              masterCode: lineItem.product.sku,
              listPrice: lineItem.listPriceFormatted,
              quantity: lineItem.quantitySatisfied,
            };
          }),
        },
      },
      basketValue: updatedBasket.formattedSubTotal,
      featuredProduct: item.product.properties.featuredProduct === '1',
      placement: PlacementEnum.BasketDetails,
      productType: ProductTypeEnum.Website,
    });
  };

  const onClear = async () => {
    try {
      setIsSaving(true);
      const updatedBasket = await clearBasket();
      setIsSaving(false);
      pushToDataLayer(GtmEventNameEnum.BasketAction);

      pushToDataLayer(GtmEventNameEnum.RemoveFromCart, {
        ecommerce: {
          remove: {
            products: updatedBasket.lineItems.map((lineItem) => {
              return {
                price: lineItem.buyPriceFormatted,
                productImage: lineItem.product.properties.webImageLarge1425,
                productName: lineItem.product.name,
                productCode: lineItem.product.variantSku,
                masterCode: lineItem.product.sku,
                listPrice: lineItem.listPriceFormatted,
                quantity: lineItem.quantitySatisfied,
              };
            }),
          },
        },
        basketValue: '£0.00',
        placement: PlacementEnum.BasketDetails,
        productType: ProductTypeEnum.Website,
      });
    } catch {
      setIsSaving(false);
    }
  };

  if (basket?.error) {
    return (
      <BasketEmpty
        title="Sorry, an error occurred"
        description="Your basket cannot be retrieved at this time, please try again later."
      />
    );
  }

  if (!basket?.lineItems?.length) {
    return (
      <BasketEmpty
        title={emptyBasketTitle}
        description={emptyBasketDescription}
      />
    );
  }

  return (
    <div className="py-component-spacing-mobile lg:pb-component-spacing lg:pt-4 flex flex-col space-y-6">
      {basketState?.quote && (
        <div className="bg-light-blue p-4 text-center rounded-lg">
          {clearQuoteWarning}
        </div>
      )}

      <div className="border-b border-grey-100 flex flex-col md:flex-row items-center md:items-end justify-between pb-4 space-y-4 md:space-y-0">
        <div>
          <h4 className="page-heading-four">{heading}</h4>
          {basketState?.quote && (
            <p>Quote reference: {basketState?.quote.id}</p>
          )}
        </div>

        <div className="flex space-x-2">
          <ButtonSecondary
            name={clearAllButtonText}
            paddingX="half"
            onClick={() => onClear()}
          />

          <ButtonSecondary
            name={updateAllButtonText}
            paddingX="half"
            onClick={() => onCommitAllLineItemQuantities()}
          />
        </div>
      </div>

      <div
        className={
          isSaving ? 'animate-pulse filter grayscale pointer-events-none' : null
        }
      >
        <BasketTable
          labels={basketTableLabels}
          checkoutLabels={checkoutBasketTableLabels}
          basketLineItems={reversedMainBasketItems}
          confirmedBackOrderSkus={confirmedBackOrderSkus}
          onToggleConfirmedBackOrderSku={(sku) =>
            toggleConfirmedBackOrderSku(sku)
          }
          onLineAdd={(item, quantity) => onLineItemAdd(item, quantity)}
          onLineItemChangeQuantity={(item, quantity) =>
            onLineItemChangeQuantity(
              item as ProductBasketLineItemType,
              quantity
            )
          }
          onLineUpdateQuantity={(item, quantity) =>
            onLineItemUpdate(item as ProductBasketLineItemType, quantity)
          }
          onLineRemove={(item) =>
            onLineItemRemove(item as ProductBasketLineItemType)
          }
          onLineReplace={(item, quantity, replacesItem) =>
            onLineItemReplace(item, quantity, replacesItem)
          }
          features={basketFeatures}
          lastAddedVariantSkus={basketState.lastAddedVariantSkus}
        />
      </div>

      {!!ownCarriageBasketItems?.length && (
        <>
          <p className="border-b border-grey-100 pb-2 text-grey-600">
            {supplierProductsHeading}
          </p>

          <div
            className={
              isSaving && 'animate-pulse filter grayscale pointer-events-none'
            }
          >
            <BasketTable
              labels={basketTableLabels}
              checkoutLabels={checkoutBasketTableLabels}
              basketLineItems={reversedOwnCarriageBasketItems}
              confirmedBackOrderSkus={confirmedBackOrderSkus}
              onToggleConfirmedBackOrderSku={(sku) =>
                toggleConfirmedBackOrderSku(sku)
              }
              onLineAdd={(item, quantity) => onLineItemAdd(item, quantity)}
              onLineItemChangeQuantity={(item, quantity) =>
                onLineItemChangeQuantity(
                  item as ProductBasketLineItemType,
                  quantity
                )
              }
              onLineUpdateQuantity={(item, quantity) =>
                onLineItemUpdate(item as ProductBasketLineItemType, quantity)
              }
              onLineRemove={(item) =>
                onLineItemRemove(item as ProductBasketLineItemType)
              }
              onLineReplace={(item, quantity, replacesItem) =>
                onLineItemReplace(item, quantity, replacesItem)
              }
              features={basketFeatures}
              lastAddedVariantSkus={basketState.lastAddedVariantSkus}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BasketDetails;
